import { toast } from 'react-toastify';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { addDays } from 'date-fns';
import _ from 'lodash';
import validator from 'email-validator';
import i18n from '../locales/i18n';
import { ProfileDataType, ProfileField } from '../types/Profile';
import { LocalContactInfo } from '../services/model/localContactService.model';
import {
  BusinessInvite,
  CustomField,
  CustomFieldResponses,
  CustomFieldType,
  CustomMultipleChoiceField,
} from '../services/model/inviteService.model';
import { filterCustomLabelFields } from './filterUtils';

export function isValidEmail(email: string): boolean {
  /* eslint-disable no-useless-escape */
  return validator.validate(email);
}

export function isEmptyEmail(field: ProfileField): boolean {
  return !field.email;
}

export function isEmptyPhoneNumber(field: ProfileField): boolean {
  return !field.phoneNumber?.prefix && !field.phoneNumber?.suffix;
}

export function isEmptyAddress(field: ProfileField): boolean {
  const { address } = field;
  if (!address) return true;
  return (
    !address.street &&
    !address.houseNumber &&
    !address.houseNumberAddition &&
    !address.city &&
    !address.postCode &&
    !address.country &&
    !address.countryCode
  );
}

export function isEmptyBirthDate(field: ProfileField): boolean {
  return !field.birthDate || field.birthDate === '--';
}

export function isEmptyField(field: ProfileField): boolean {
  switch (field.dataType) {
    case 'EMAIL':
      return isEmptyEmail(field);
    case 'PHONENUMBER':
      return isEmptyPhoneNumber(field);
    case 'ADDRESS':
      return isEmptyAddress(field);
    case 'BIRTHDATE':
      return isEmptyBirthDate(field);
    default:
      return false;
  }
}

export function isValidProfileFields(fields: ProfileField[]): boolean {
  const invalid = fields
    .filter((field): boolean => !IsValidField(field))
    .map((field): string => field.dataType);
  if (invalid.length === 0) return true;
  if (invalid.includes(ProfileDataType.EMAIL)) {
    toast.error(i18n.t('toast.error.field.email'));
    return false;
  }
  if (invalid.includes(ProfileDataType.PHONENUMBER)) {
    toast.error(i18n.t('toast.error.field.phoneNumber'));
    return false;
  }
  if (invalid.includes(ProfileDataType.ADDRESS)) {
    toast.error(i18n.t('toast.error.field.address'));
    return false;
  }
  if (invalid.includes(ProfileDataType.BIRTHDATE)) {
    toast.error(i18n.t('toast.error.field.birthDate'));
    return false;
  }
  return true;
}

export function IsValidEmailField(field: ProfileField, canBeEmpty: boolean): boolean {
  return (canBeEmpty && isEmptyEmail(field)) || isValidEmail(field.email!);
}

export function IsValidPhoneNumberField(field: ProfileField, canBeEmpty: boolean): boolean {
  return (
    (canBeEmpty && isEmptyPhoneNumber(field)) ||
    isValidPhoneNumber(`${field.phoneNumber?.prefix}${field.phoneNumber?.suffix}`)
  );
}

export function IsValidAddressField(field: ProfileField, canBeEmpty: boolean): boolean {
  if (canBeEmpty && isEmptyAddress(field)) return true;
  const { address } = field;
  if (!address) return false;

  return (
    !!address.street &&
    !!address.houseNumber &&
    !!address.city &&
    !!address.postCode &&
    !!address.country &&
    !!address.countryCode
  );
}

export function IsValidBirthDateField(field: ProfileField, canBeEmpty: boolean): boolean {
  const [year, month, day] = field.birthDate?.split('-') || [];
  if (!year && !month && !day) return canBeEmpty;
  if (Number.isNaN(+year) || Number.isNaN(+month) || Number.isNaN(+day)) return false;
  if (!year || !month || !day) return false;
  if (+month < 1 || +month > 12) return false;
  if (+day < 1 || +day > 31) return false;
  const currentDate = new Date();
  const selectedDate = new Date(+year, +month - 1, +day);
  currentDate.setHours(0, 0, 0, 0);

  if (selectedDate > currentDate) return false;
  return true;
}

export function IsValidField(field: ProfileField, canBeEmpty: boolean = true): boolean {
  switch (field.dataType) {
    case ProfileDataType.EMAIL:
      return IsValidEmailField(field, canBeEmpty);
    case ProfileDataType.PHONENUMBER:
      return IsValidPhoneNumberField(field, canBeEmpty);
    case ProfileDataType.ADDRESS:
      return IsValidAddressField(field, canBeEmpty);
    case ProfileDataType.BIRTHDATE:
      return IsValidBirthDateField(field, canBeEmpty);
    case ProfileDataType.SOCIAL:
    case ProfileDataType.BUSINESSNAME:
      return true;
    default:
      return false;
  }
}

export function isValidLocalContact(info: LocalContactInfo, invite: BusinessInvite): boolean {
  if (!info.firstName || !info.lastName) return false;
  if (info.receivedData.filter((f) => !isEmptyField(f)).some((f) => !IsValidField(f))) return false;
  if (invalidCustomFieldResponses(invite.customFields, info.customFieldResponses).length)
    return false;

  return true;
}

export function invalidCustomFieldResponses(
  fields: CustomField[],
  response: CustomFieldResponses,
): CustomField[] {
  const customFields = filterCustomLabelFields(fields);
  const invalidMC = (
    customFields.filter(
      (f) => f.type === CustomFieldType.MULTIPLE_CHOICE,
    ) as CustomMultipleChoiceField[]
  ).filter(
    (f: CustomMultipleChoiceField): boolean =>
      !!response[f.customFieldId!] && !f.options.includes(response[f.customFieldId!]),
  );
  return invalidMC;
}

export function isEqualBusinessInvite(invite1: BusinessInvite, invite2: BusinessInvite): boolean {
  return (
    _.isEqual(new Set(invite1.mandatoryFields), new Set(invite2.mandatoryFields)) &&
    _.isEqual(new Set(invite1.customFields), new Set(invite2.customFields)) &&
    _.isEqual(new Set(invite1.optionalFields), new Set(invite2.optionalFields)) &&
    _.isEqual(new Set(invite1.nonRequestedFields), new Set(invite2.nonRequestedFields))
  );
}

/**
 * Checks if the given string is a valid URL
 * @param string
 */
export function isValidHttpUrl(string: string) {
  const attempts = [string, `https://${string}`, `http://${string}`];

  if (!string.includes('.')) return false; // No domain (e.g. 'google' instead of 'google.com')

  for (let i = 0; i < attempts.length; i += 1) {
    const attempt = attempts[i];
    try {
      const possibleUrl = new URL(attempt);
      return possibleUrl.protocol === 'http:' || possibleUrl.protocol === 'https:';
    } catch (e) {
      // Do nothing
    }
  }

  return false;
}

/**
 * Formats the given url to a valid url by adding 'https://' if it's missing
 * @param url
 */
export function formatUrl(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  return `https://${url}`;
}

export function withinDateBounds<T extends { sendDate: Date | string }>(
  communication: T,
  startDate: Date | undefined,
  endDate: Date | undefined,
) {
  if (!startDate || !endDate) return true;
  if ('orderId' in communication) {
    return (
      addDays(new Date(communication.sendDate), 1) >= startDate &&
      addDays(new Date(communication.sendDate), 1) <= endDate
    );
  }
  return (
    new Date(communication.sendDate) >= startDate && new Date(communication.sendDate) <= endDate
  );
}

/**
 * Validates an IBAN (International Bank Account Number)
 * @param iban - The IBAN string to validate
 * @returns true if the IBAN is valid, false otherwise
 */
export function isValidIBAN(iban: string): boolean {
  if (!iban || typeof iban !== 'string') return false;

  // Remove spaces and convert to uppercase
  const cleanIban = iban.replace(/\s/g, '').toUpperCase();

  // Special exception for test bank account
  if (cleanIban === 'GL6572120000072123' || cleanIban === 'GL2154350000054355') {
    return true;
  }

  // Check basic format: 2 letters + 2 digits + up to 30 alphanumeric characters
  const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{4,30}$/;
  if (!ibanRegex.test(cleanIban)) return false;

  // Check minimum and maximum length (15-34 characters)
  if (cleanIban.length < 15 || cleanIban.length > 34) return false;

  // Move first 4 characters to end
  const rearranged = cleanIban.slice(4) + cleanIban.slice(0, 4);

  // Replace letters with numbers (A=10, B=11, ..., Z=35)
  const numericString = rearranged.replace(/[A-Z]/g, (char) =>
    (char.charCodeAt(0) - 55).toString(),
  );

  // Calculate mod 97
  let remainder = 0;
  for (let i = 0; i < numericString.length; i += 1) {
    remainder = (remainder * 10 + parseInt(numericString[i], 10)) % 97;
  }

  return remainder === 1;
}

/**
 * Validates if an IBAN belongs to a specific country
 * @param iban - The IBAN string to validate
 * @param countryCode - The two-letter country code (e.g., 'NL', 'DE', 'BE')
 * @returns true if the IBAN is valid and belongs to the specified country
 */
export function isValidIBANForCountry(iban: string, countryCode: string): boolean {
  if (!isValidIBAN(iban)) return false;

  const cleanIban = iban.replace(/\s/g, '').toUpperCase();
  return cleanIban.startsWith(countryCode.toUpperCase());
}

/**
 * Validates if an IBAN belongs to a specific bank based on BIC codes
 * @param iban - The IBAN string to validate
 * @param bank - The bank enum value
 * @returns true if the IBAN appears to belong to the specified bank
 */
export function isValidIBANForBank(iban: string, bank: string): boolean {
  if (!isValidIBAN(iban)) return false;

  const cleanIban = iban.replace(/\s/g, '').toUpperCase();

  // Special exception for test bank account - always allow with any bank
  if (cleanIban === 'GL6572120000072123' || cleanIban === 'GL2154350000054355') {
    return true;
  }

  // Dutch IBANs start with NL
  if (!cleanIban.startsWith('NL')) return false;

  // Extract bank code from Dutch IBAN (positions 4-7)
  const bankCode = cleanIban.substring(4, 8);

  // Map of Dutch bank codes to bank names
  const bankCodes: Record<string, string[]> = {
    RABO: ['RABOBANK'],
    ABNA: ['ABN_AMRO'],
    ASNB: ['ASN_BANK'],
    INGB: ['ING'],
    BUNQ: ['BUNQ'],
    SNSB: ['SNS_BANK'],
  };

  // Check if the bank code matches the selected bank
  const bankCodeEntries = Object.entries(bankCodes);
  for (let i = 0; i < bankCodeEntries.length; i += 1) {
    const [code, banks] = bankCodeEntries[i];
    if (bankCode === code && banks.includes(bank)) {
      return true;
    }
  }

  return false;
}

/**
 * Formats an IBAN with spaces for better readability
 * @param iban - The IBAN string to format
 * @returns Formatted IBAN with spaces every 4 characters
 */
export function formatIBAN(iban: string): string {
  if (!iban) return '';

  const cleanIban = iban.replace(/\s/g, '').toUpperCase();
  return cleanIban.replace(/(.{4})/g, '$1 ').trim();
}

/**
 * Validates a Dutch bank account number (IBAN)
 * @param iban - The IBAN string to validate
 * @param t - Translation function for localized error messages
 * @returns Object with validation result and error message
 */
export function validateDutchIBAN(
  iban: string,
  t?: (key: string) => string,
): { isValid: boolean; error?: string } {
  if (!iban || typeof iban !== 'string') {
    return { isValid: false, error: t ? t('validation.ibanRequired') : 'IBAN is required' };
  }

  const cleanIban = iban.replace(/\s/g, '').toUpperCase();

  // Special exception for test bank account in development
  if (cleanIban === 'GL6572120000072123' || cleanIban === 'GL2154350000054355') {
    return { isValid: true };
  }

  // Check if it starts with NL
  if (!cleanIban.startsWith('NL')) {
    return {
      isValid: false,
      error: t ? t('validation.dutchIbanRequired') : 'Dutch IBAN must start with NL',
    };
  }

  // Check length (Dutch IBANs are 18 characters)
  if (cleanIban.length !== 18) {
    return {
      isValid: false,
      error: t ? t('validation.dutchIbanLength') : 'Dutch IBAN must be 18 characters long',
    };
  }

  // Check format: NL + 2 digits + 4 letters + 10 digits
  const dutchIbanRegex = /^NL[0-9]{2}[A-Z]{4}[0-9]{10}$/;
  if (!dutchIbanRegex.test(cleanIban)) {
    return {
      isValid: false,
      error: t ? t('validation.dutchIbanFormat') : 'Invalid Dutch IBAN format',
    };
  }

  // Validate using mod-97 check
  if (!isValidIBAN(cleanIban)) {
    return { isValid: false, error: t ? t('validation.ibanChecksum') : 'Invalid IBAN checksum' };
  }

  return { isValid: true };
}
